import React from "react";

import { Pagination } from "@performyard/py-components";

import FilterChips from "../../../../components/FilterChips";
import PageHeading from "../../../../components/PageHeading";
import Spacer from "../../../../components/Spacer";
import ActionItemsFilterPanel from "./ActionItemsFilterPanel";
import ActionItemsTable from "./ActionItemsTable";

import { type ActionItemsFilters } from "../schemas";

import { useActionItemsSearch, useCurrentUser, useMeetingWorkspacesSearch } from "../../../../api/hooks";
import { useIsNewNavigation } from "../../../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { ActionItemsSearchSortableFieldsEnum } from "../../../../api/services/defs/internal-api";

import { convertActionItemFiltersToFilterChips, convertActionItemFiltersToSearchParams } from "../utils";

import InfoPageTemplate from "../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../../../Error";
import { actionItemsTableConfig } from "../config";

const PAGE_TITLE = "My Action Items";

const ActionItems = () => {
	const isNewNav = useIsNewNavigation();
	const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false);
	const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
	const { value: ffMeetings, query: ffMeetingsQuery } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	// Query state for the page (filters, sort, search, page)
	const {
		filters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<ActionItemsFilters>({
		defaultSortName: actionItemsTableConfig.defaultSortName,
		defaultSortDirection: actionItemsTableConfig.defaultSortDirection,
		sortableFields: ActionItemsSearchSortableFieldsEnum.options,
		defaultPageNumber: actionItemsTableConfig.defaultPageNumber,
		fallbackFilters: actionItemsTableConfig.fallbackFilters,
	});

	// Make sure status is always included (in case we receive a bad URL)
	if (filters.status === undefined) {
		setFilters({ ...filters, status: "incomplete" });
	}

	// Current user ID
	const userId = currentUser?.me?.authorization?.payload.user_id || "";

	// Ensure status is always set
	const normalizedFilters: ActionItemsFilters = {
		...filters,
		status: filters.status || "incomplete",
	};

	const {
		data: actionItems,
		totalCount: totalActionItemsCount,
		isLoading: isFetchingActionItems,
	} = useActionItemsSearch(
		convertActionItemFiltersToSearchParams(normalizedFilters, debouncedSearchValue, sort, pageNumber)
	);

	// Get workspace name for filter chips
	const { data: workspaces } = useMeetingWorkspacesSearch({
		participantIds: userId ? [userId] : [],
		statusEq: "active",
		limit: 100,
	});
	const selectedWorkspaceName = normalizedFilters.workspace
		? workspaces?.find((ws) => ws._id === normalizedFilters.workspace)?.name
		: undefined;

	// Wait for feature flag to resolve before deciding access — otherwise the
	// `false` default flashes a 403 page on first render.
	if (ffMeetingsQuery.isLoading) {
		return null;
	}

	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	const actionButtons: React.ReactNode[] = [];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			searchPlaceholder="Search action items..."
			onFilterButtonPress={() => setIsFilterPanelOpen(true)}
		/>
	);

	const toolbarInformationRow = (
		<FilterChips
			filters={convertActionItemFiltersToFilterChips(normalizedFilters, selectedWorkspaceName)}
			label="Filters applied: "
			onDeleteFilter={(id) => {
				const currentFilters: ActionItemsFilters = { ...normalizedFilters };
				// Default to incomplete status if status is deleted
				if (id === "status") {
					currentFilters.status = "incomplete";
				} else if (id === "dueDateRange") {
					delete currentFilters.dueDateRange;
				} else {
					delete currentFilters[id as keyof ActionItemsFilters];
				}
				setFilters(currentFilters);
			}}
			nonDismissibleFilters={normalizedFilters.status === "incomplete" ? ["status"] : []} // Incomplete is not dismissible
		/>
	);

	const filterPanel = (
		<ActionItemsFilterPanel
			isOpen={isFilterPanelOpen}
			onClose={() => setIsFilterPanelOpen(false)}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
			filters={normalizedFilters}
		/>
	);

	const mainContent = (
		<div>
			{actionItems && (
				<>
					<ActionItemsTable
						actionItems={actionItems}
						isFetchingActionItems={isLoadingUser || isFetchingActionItems}
						activeSort={sort.name}
						activeSortDirection={sort.direction}
						handleColumnHeaderClick={(sortValue) => setSort(sortValue)}
						userId={userId}
					/>
					<Spacer height={3} />
					<div data-testid="paginationContainer" className="mx-auto flex w-full max-w-[400px] justify-center">
						<Pagination
							currentPage={pageNumber}
							pageCount={Math.ceil(totalActionItemsCount / actionItemsTableConfig.pageSize)}
							onPageChange={(newPage: number) => setPageNumber(newPage)}
						/>
					</div>
				</>
			)}
		</div>
	);

	return (
		<>
			<InfoPageTemplate
				employeeID={userId}
				pageTitle={PAGE_TITLE}
				pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
				mainContent={mainContent}
				actionButtons={actionButtons}
				toolbarActionRow={toolbarActionRow}
				toolbarInformationRow={toolbarInformationRow}
				navigationLinks={[
					{ key: "my-workspaces", label: "Workspaces I'm In", href: "/meeting-workspaces" },
					{ key: "action-items", label: "Action Items", href: "/meeting-workspaces?tab=action-items" },
				]}
				activeNavigationLinkKey="action-items"
				drawer={filterPanel}
			/>
			<Spacer height={5} />
		</>
	);
};

export default ActionItems;
